import React, { memo, useEffect, useState } from "react";
import PageTrack from "components/page-track";
import Card from "components/horse-info";
import RenderModules from "helpers/render-modules";
import BaseLayout from "layouts/base-layout";
import ogrLogo from "images/ogr_logo_black.png";
import { SEO } from "../components/SEO";
import { MetaPixel, TiktokPixel, Klaviyo, HotjarPixel, AdsenseVerification } from "../utils/pixel";
import { selectedHorseStore } from "store/selectedHorse";
import AutomaticSignUp from "components/automatic-sign-up";
import { sendDataLayerEvent } from "../helpers/send-event";

const shouldSignUp = (horse) => {
  const horsesThatHaveSignUpBySlug = ["the-club-horse", "henry-box-brown"];
  return horsesThatHaveSignUpBySlug.includes(horse.slug);
}

const Horse = memo(({ pageContext: { content, ...rest }, location }) => {
  const { setSelectedHorse } = selectedHorseStore();

  const { modules } = content || {};

  const imagesGalleryModule = modules?.filter(
    (obj) => obj._type === "imagesGallery"
  );
  const newsletterModule = modules?.filter((obj) => obj._type === "newsletter");

  const footerCtaModule = modules?.filter((obj) => obj._type === "footerCta");

  const otherModules = modules?.filter(
    (obj) =>
      obj._type !== "imagesGallery" &&
      obj._type !== "newsletter" &&
      obj._type !== "footerCta"
  );

  const { meta, settings } = rest;

  const paths = [
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Horses",
      path: "/horses",
    },
    {
      name: rest.name,
      path: `/horses/${rest.slug}`,
    },
  ];

  useEffect(() => {
    setSelectedHorse({ content, ...rest });

    const {
      share_price,
      name,
      backpack_id
    } = rest;

    sendDataLayerEvent("view_item", {
        value: share_price,
        items: [{
            item_id: backpack_id,
            item_name: name,
            item_category: 'horse',
            price: share_price,
            quantity: 1
        }]
    })

    return () => setSelectedHorse(null);
  }, []);


  return (
    <BaseLayout meta={meta} data={settings} location={location}>
      <div className="pt-8 pb-36">
        <div className="container flex justify-between items-center ">
          <div>
            {paths && <PageTrack paths={paths} />}
            <div className="flex items-center mt-1">
              <h1 className="header">{rest.name}</h1>
              <span className="px-3 py-1 bg-gray-400 bg-opacity-20 rounded-xl ml-2">
                <h4 className="font-sans font-medium text-sm text-gray-500">
                  {rest?.discipline}
                </h4>
              </span>
            </div>
          </div>
          <div className="max-w-[200px] sm:hidden">
            {!rest.is_racing_club && <p className="font-sans font-medium text-sm text-gray-500 mb-1 text-right">
              Syndicated by:
            </p>}
            <img
              src={rest.syndicateImage?.src || rest.horse_logo || ogrLogo}
              alt="Syndicated image"
              className="max-h-12 object-cover ml-auto"
            />
          </div>
        </div>
        {imagesGalleryModule && <RenderModules modules={imagesGalleryModule} />}
        <div className="container flex items-start gap-6 mt-9">
          <div className="w-2/3 sm:w-full">
            {otherModules && <RenderModules modules={otherModules} />}
          </div>
          {rest.slug !== "retired-horses" && (
            <div className="sticky top-32 w-1/3 shadow-horsePrice bg-gray-100 p-4 rounded-2xl sm:top-auto sm:bottom-0 sm:fixed sm:-mx-4 sm:w-full sm:z-[5]">
              {shouldSignUp(rest) ? <AutomaticSignUp /> : <Card content={rest} />}
            </div>
          )}
        </div>
      </div>
      {newsletterModule && <RenderModules modules={newsletterModule} />}
      {footerCtaModule && <RenderModules modules={footerCtaModule} />}
    </BaseLayout>
  );
});

export const Head = ({ pageContext }) => {
  const { meta, cardDescription, name, backpack_id, share_price, slug } = pageContext;

  return (
    <>
      <MetaPixel sendEvent {...{
        content_name: name,
        content_ids: [backpack_id],
        content_type: "product",
        value: share_price,
        currency: "GBP"
      }} />
      {slug == "the-club-horse" && (
        <HotjarPixel />
      )}
      <TiktokPixel />
      <AdsenseVerification />
      <Klaviyo />
      <SEO
        meta={meta}
        path="horses"
        handle={pageContext.slug}
        additionalTitle={name}
        additionalDescription={cardDescription}
      />
    </>
  );
};

export default Horse;
